import React, { useEffect, useState } from "react"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "src/components/ui/dialog"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Label } from "src/components/ui/label"
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "src/components/ui/select"
import { useToast } from "src/components/ui/toast"
import { updateDeal } from "../api/dealsApi"
import { fetchMembers } from "src/features/members/api/membersApi"

// Edits a deal's own fields (name, value, owner, expected close) plus the
// linked lead's email and budget (which live on the Lead, not the Deal — the
// backend writes those through to the lead).
export function EditDealDialog({ open, deal, onClose, onSaved }) {
  const { toast } = useToast()
  const [saving, setSaving] = useState(false)
  const [members, setMembers] = useState([])

  const [form, setForm] = useState({
    name: "", value: "", owner_user_id: null, expected_close_on: "", email: "", budget: "",
  })

  useEffect(() => {
    if (!open || !deal) return
    setForm({
      name: deal.name || "",
      value: deal.value ?? "",
      owner_user_id: deal.owner_user_id ?? deal.owner?.id ?? null,
      expected_close_on: deal.expected_close_on ? deal.expected_close_on.slice(0, 10) : "",
      email: deal.lead?.email || "",
      budget: deal.lead?.budget ?? "",
    })
  }, [open, deal])

  useEffect(() => {
    if (!open) return
    let active = true
    fetchMembers({ per_page: 100 })
      .then((d) => { if (active) setMembers(d.memberships || []) })
      .catch(() => { if (active) setMembers([]) })
    return () => { active = false }
  }, [open])

  function set(key, val) {
    setForm((f) => ({ ...f, [key]: val }))
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast({ variant: "destructive", title: "Deal name is required" })
      return
    }
    setSaving(true)
    try {
      const payload = {
        name: form.name.trim(),
        value: form.value === "" ? null : Number(form.value),
        owner_user_id: form.owner_user_id || null,
        expected_close_on: form.expected_close_on || null,
        email: form.email.trim() || null,
        budget: form.budget === "" ? null : Number(form.budget),
      }
      const updated = await updateDeal(deal.id, payload)
      toast({ title: "Deal updated" })
      onSaved?.(updated)
      onClose()
    } catch (errors) {
      const msg = Array.isArray(errors) ? errors[0] : errors?.response?.data?.errors?.[0] || "Could not update deal"
      toast({ variant: "destructive", title: "Update failed", description: msg })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit deal</DialogTitle>
          <DialogDescription>
            Update the deal details. Email and budget are saved on the linked lead.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label>Deal name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Value (₹)</Label>
              <Input type="number" min="0" placeholder="0" value={form.value} onChange={(e) => set("value", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Budget (₹)</Label>
              <Input type="number" min="0" placeholder="0" value={form.budget} onChange={(e) => set("budget", e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Owner</Label>
            <Select
              value={form.owner_user_id ? String(form.owner_user_id) : ""}
              onValueChange={(v) => set("owner_user_id", Number(v))}
            >
              <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                {members.map((m) => (
                  <SelectItem key={m.user.id} value={String(m.user.id)}>
                    {m.user.name || m.user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" placeholder="customer@example.com" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Expected close</Label>
              <Input type="date" value={form.expected_close_on} onChange={(e) => set("expected_close_on", e.target.value)} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
